import { ChevronRight, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,

  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { WorkspaceLink } from "@/components/ui/workspace-link";

export function NavMain({
  items,
  dataTourMap = {},
  collapsed = false,
  onLinkClick,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    badge?: React.ReactNode;
    items?: {
      title: string;
      url: string;
      badge?: React.ReactNode;
      icon?: LucideIcon;
      isActive?: boolean;
    }[];
  }[];
  groupLabel?: string;
  dataTourMap?: Record<string, string>;
  collapsed?: boolean;
  onLinkClick?: () => void;
}) {
  // Initialize with all items open by default
  const [openStates, setOpenStates] = useState<Record<string, boolean>>(() => {
    const savedStates = localStorage.getItem("navMainOpenStates");
    if (savedStates) {
      return JSON.parse(savedStates);
    }
    // Set all items with subitems to be open by default
    return items.reduce((acc, item) => {
      if (item.items?.length) {
        acc[item.title] = true;
      }
      return acc;
    }, {} as Record<string, boolean>);
  });

  // Save states to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("navMainOpenStates", JSON.stringify(openStates));
  }, [openStates]);

  const handleToggle = (title: string, isOpen: boolean) => {
    const newStates = { ...openStates, [title]: isOpen };
    setOpenStates(newStates);
  };

  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) =>
          item.items?.length ? (
            <Collapsible
              key={item.title}
              asChild
              open={openStates[item.title]}
              onOpenChange={(isOpen) => handleToggle(item.title, isOpen)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={item.isActive}
                    data-tour={dataTourMap[item.title]}
                    className={cn(!item.isActive && "text-muted-foreground")}
                  >
                    {item.icon && <item.icon />}
                    {!collapsed && <span>{item.title}</span>}
                    {!collapsed && item.badge}
                    {!collapsed && (
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                {!collapsed && (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem
                          className="text-sm group/subitem"
                          key={subItem.title}
                        >
                          <SidebarMenuSubButton
                            asChild
                            className="group/subbutton"
                            isActive={subItem.isActive}
                          >
                            <WorkspaceLink
                              to={subItem.url}
                              onClick={handleLinkClick}
                              className="flex items-center w-full gap-2 min-w-0"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                                {subItem.icon && (
                                  <subItem.icon className="h-4 w-4 shrink-0" />
                                )}
                                <span className="whitespace-nowrap truncate">
                                  {subItem.title}
                                </span>
                              </div>
                              {subItem.badge && (
                                <span className="shrink-0 flex items-center ml-auto">
                                  {subItem.badge}
                                </span>
                              )}
                            </WorkspaceLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                tooltip={item.title}
                asChild
                isActive={item.isActive}
                data-tour={dataTourMap[item.title]}
                className={cn(!item.isActive && "text-muted-foreground")}
              >
                <WorkspaceLink to={item.url} onClick={handleLinkClick}>
                  {item.icon && <item.icon />}
                  {!collapsed && <span>{item.title}</span>}
                  {!collapsed && item.badge}
                </WorkspaceLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
